import axios from "axios";
import ApiClient from "./ApiClient";
import NewUUID from "../utils/randy/NewUUID";
import { getTurnstileToken } from './turnstile';

export const confirmEmail = (email, verification_code, csrfToken) => {
    return ApiClient.post(
        '/api/auth/confirm_email',
        {
            email: email,
            code: verification_code
        }, {
            headers: {
                accept: 'application/json',
                request_id: NewUUID(),
                "X-CSRF-Token": csrfToken
            },
        }
    );
};

/**
 * Refresh the auth token (extends session). Backend requires Email in the request body.
 * @param {string} csrfToken
 * @param {string} email - User's email (must be stored in auth claims when they log in)
 */
export const refreshAuthToken = (csrfToken, email) => {
    if (!email || typeof email !== 'string' || !email.trim()) {
        return Promise.reject(new Error('Email is required for refresh'));
    }
    return ApiClient.post('/api/auth/refresh', { email: email.trim() }, {
        headers: {
            accept: "application/json",
            request_id: NewUUID(),
            "X-CSRF-Token": csrfToken
        }
    }).then(response => {
        // The backend should set new 'claims' and 'SessionTimer' cookies
        // Return the full response object so we can access headers for parsing claims
        return response;
    }).catch(error => {
        // Only log non-401 errors as actual errors (401 is expected when refresh token expires)
        if (error.response?.status !== 401) {
            console.error('Error refreshing auth token:', error);
        }
        throw error;
    });
}

export const logout = (csrfToken) => {
    return ApiClient.post('/api/auth/logout', {}, {
        headers: {
            accept: "application/json",
            request_id: NewUUID(),
            "X-CSRF-Token": csrfToken
        }
    });
}

export const resendConfirmEmail = (email, csrfToken) => {
    return ApiClient.post(
        '/api/auth/resend_confirm_email',
        {
            email: email,
        }, {
            headers: {
                accept: 'application/json',
                request_id: NewUUID(),
                "X-CSRF-Token": csrfToken
            },
        }
    );
};

export const sendResetPass = (email, password, verification_code, csrfToken) => {
    return ApiClient.post(
        '/api/auth/forgot_password/confirm',
        {
            email: email,
            new_password: btoa(password),
            confirmation_code: verification_code
        }, {
            headers: {
                accept: 'application/json',
                request_id: NewUUID(),
                "X-CSRF-Token": csrfToken
            },
        }
    );
};

export const triggerResetPassToken = (email, csrfToken) => {
    return ApiClient.post(
        '/api/auth/forgot_password',
        {
            email: email,
        }, {
            headers: {
                accept: 'application/json',
                request_id: NewUUID(),
                "X-CSRF-Token": csrfToken
            },
        }
    );
};

export const setupMFA = (email, csrfToken) => {
    return ApiClient.post(
        '/api/auth/mfa/setup',
        {
            email: email,
        }, {
            headers: {
                accept: 'application/json',
                request_id: NewUUID(),
                "X-CSRF-Token": csrfToken
            },
        }
    );
};

export const verifyMFACode = (email, mfaCode, csrfToken) => {
    return ApiClient.post(
        '/api/auth/mfa/verify',
        {
            email: email,
            code: mfaCode
        }, {
            headers: {
                accept: 'application/json',
                request_id: NewUUID(),
                "X-CSRF-Token": csrfToken
            },
        }
    );
};

export const login = (email, password, csrfToken) => {
    return ApiClient.post(
        '/api/auth/login',
        {
            email: email,
            password: btoa(password),
            // Single-use Turnstile token. Read at call time rather than passed in, so
            // the auth-flow hook chain does not have to thread it (see services/turnstile.js).
            // The server fails closed if this is absent, so never omit it conditionally.
            'cf-turnstile-response': getTurnstileToken()
        }, {
            headers: {
                accept: 'application/json',
                request_id: NewUUID(),
                "X-CSRF-Token": csrfToken
            },
        }
    );
};

export const signup = (email, name, password, csrfToken) => {
    return ApiClient.post(
        '/api/auth/signup',
        {
            email: email,
            name: name,
            password: btoa(password),
            'cf-turnstile-response': getTurnstileToken()
        }, {
            headers: {
                accept: 'application/json',
                request_id: NewUUID(),
                "X-CSRF-Token": csrfToken
            },
        }
    );
};

export const completeSignup = (name, email, password, verification_code, csrfToken) => {
    return ApiClient.post(
        '/api/auth/signup',
        {
            name: name,
            email: email,
            password: btoa(password),
            verification_code: verification_code,
            'cf-turnstile-response': getTurnstileToken()
        }, {
            headers: {
                accept: 'application/json',
                request_id: NewUUID(),
                "X-CSRF-Token": csrfToken
            },
        }
    );
};

export const verifyMFAWithSession = (email, mfaUserCode, mfaSessionId, extUserId, csrfToken) => {
    return ApiClient.post(
        '/api/auth/mfa/verify',
        {
            email: email,
            mfa_user_code: mfaUserCode,
            mfa_session_id: mfaSessionId,
            ext_user_id: extUserId,
        },
        {
            headers: {
                accept: 'application/json',
                request_id: NewUUID(),
                "X-CSRF-Token": csrfToken
            },
        }
    );
};
